import { useState, useCallback } from "react";
import { Share2 } from "lucide-react";
import ShareModal from "../Navigation/Sidebar/ShareModal";

const APP_BLUE = "#1c9dea";

/**
 * ShareArticleButton — A reusable button that opens the ShareModal
 * in article-sharing mode. Drop this anywhere you have a document_id.
 *
 * Props:
 *   documentId (string, required) — the article's document_id
 *   article (object, required) — the full article object (for snapshot data)
 *   variant ("pill" | "icon") — pill = styled like like/dislike/bookmark, icon = bare icon button
 *   className (string) — additional classes
 *   active (bool) — for visual consistency with ActionPill siblings (always false by default)
 */
const ShareArticleButton = ({
  documentId,
  article,
  variant = "pill",
  className = "",
  iconSize = 20,
  strokeWidth = 1.5,
}) => {
  const [showModal, setShowModal] = useState(false);

  const handleClick = useCallback((e) => {
    e.stopPropagation();
    setShowModal(true);
  }, []);

  const articleSnapshot = article
    ? {
        title: article.title || "",
        url: article.url || "",
        source: article.source || "",
        screenshot: article.screenshot || "",
        screenshot_full: article.screenshot_full || "",
        target_type: article.target_type || "",
        target: article.target || "",
        content: (article.content || "").slice(0, 300),
        timestamp: article.timestamp || "",
        apt_name: article.apt_name || "",
      }
    : {};

  const itemName = article?.title || article?.url || "Article";

  if (variant === "icon") {
    return (
      <>
        <button
          type="button"
          aria-label="Share article"
          onClick={handleClick}
          className={[
            "inline-flex items-center justify-center p-1.5 rounded-md transition-colors",
            "hover:bg-zinc-100 dark:hover:bg-zinc-800",
            className,
          ].join(" ")}
        >
          <Share2
            size={iconSize}
            strokeWidth={strokeWidth}
            style={{ color: "rgb(203 213 225)" }}
          />
        </button>

        {showModal && (
          <div
            className="fixed inset-0 z-50 flex items-center justify-center bg-black/50"
            onClick={() => setShowModal(false)}
          >
            <ShareModal
              onClose={() => setShowModal(false)}
              contentType="article"
              documentId={documentId}
              itemName={itemName}
              articleSnapshot={articleSnapshot}
            />
          </div>
        )}
      </>
    );
  }

  // variant === "pill" — matches ActionPill styling from ArticleCard
  return (
    <>
      <button
        type="button"
        aria-label="Share article"
        onClick={handleClick}
        className={[
          "inline-flex items-center justify-center rounded-full h-9 w-9 border backdrop-blur-sm transition-all duration-200",
          "active:scale-95 focus:outline-none focus:ring-2 focus:ring-[#1c9dea]/40",
          "border-[#0EA5E9]/25 bg-gray-900/80 hover:scale-105 hover:border-[#38BDF8]/40",
          className,
        ].join(" ")}
      >
        <Share2
          className="h-5 w-5"
          strokeWidth={strokeWidth}
          style={{ color: "rgb(203 213 225)" }}
        />
      </button>

      {showModal && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/50"
          onClick={() => setShowModal(false)}
        >
          <ShareModal
            onClose={() => setShowModal(false)}
            contentType="article"
            documentId={documentId}
            itemName={itemName}
            articleSnapshot={articleSnapshot}
          />
        </div>
      )}
    </>
  );
};

export default ShareArticleButton;
